'use client';

import { useState, useRef } from 'react';
import { Box, Typography, ButtonBase, InputBase, CircularProgress } from '@mui/material';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Icon } from '@/components/icons';
import { useTranslator, useAppLanguage } from '@/lang';
import { getInquiryTypes, createInquiry } from '../../api/inquiryApi';
import TypeSelectDropdown from './TypeSelectDropdown';
import { useToastStore } from '@/stores/toastStore';
import { useDarkColors } from '@/hooks/useDarkColors';
import {
  TEMP_TEXT_BOLD_XL,
  TEMP_TEXT_MEDIUM_M,
  TEMP_TEXT_MEDIUM_XL,
  TEMP_TEXT_REGULAR_XS,
} from '@/constants/tempTypo';

const MAX_CONTENT_LENGTH = 1000;
const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface InquiryContentProps {
  onBack?: () => void;
  initialTypeKey?: string;
}

export default function InquiryContent({ onBack, initialTypeKey }: InquiryContentProps) {
  const { t, f } = useTranslator();
  const { language } = useAppLanguage();
  const c = useDarkColors();

  const [email, setEmail] = useState('');
  const [selectedTypeKey, setSelectedTypeKey] = useState(initialTypeKey ?? '');
  const [showTypeSelect, setShowTypeSelect] = useState(false);
  const [content, setContent] = useState('');
  const [images, setImages] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const showToast = useToastStore((s) => s.show);

  // 문의 유형 목록 조회
  const { data: typesData, isLoading: isLoadingTypes } = useQuery({
    queryKey: ['inquiryTypes'],
    queryFn: getInquiryTypes,
    staleTime: 1000 * 60 * 30, // 30분 캐시
  });

  // 문의 등록 mutation
  const submitMutation = useMutation({
    mutationFn: createInquiry,
    onSuccess: () => {
      showToast(
        t('inquiry.toast_title'),
        t('inquiry.toast_message'),
      );
      onBack?.();
    },
    onError: () => {
      showToast(
        t('inquiry.error_title'),
        t('inquiry.error_submit'),
      );
    },
  });

  // 회원탈퇴는 헤더의 별도 진입점에서 처리하므로 문의 유형에서는 제외
  const inquiryTypes = typesData?.types ?? [];

  // label이 object 형태 { ko: string, en: string, ja: string } 일 수 있음
  const getTypeLabel = (label: unknown): string => {
    if (typeof label === 'string') return label;
    if (label && typeof label === 'object' && language in (label as Record<string, unknown>)) {
      return (label as Record<string, string>)[language];
    }
    // fallback: 첫 번째 값
    if (label && typeof label === 'object') {
      const values = Object.values(label as Record<string, string>);
      return values[0] ?? '';
    }
    return '';
  };

  const selectedTypeLabel = inquiryTypes.find((t) => t.key === selectedTypeKey);

  const isEmailValid = EMAIL_REGEX.test(email.trim());
  const isFormValid = isEmailValid && selectedTypeKey !== '' && content.trim() !== '';

  const handleImageAdd = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const validFiles = Array.from(files).filter((file) => {
      if (file.size > MAX_IMAGE_SIZE) {
        showToast(t('inquiry.error_title'), t('inquiry.error_image_size'));
        return false;
      }
      return true;
    });
    const newImages = [...images, ...validFiles].slice(0, 3);
    setImages(newImages);
    e.target.value = '';
  };

  const handleRemoveImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    if (!isFormValid || submitMutation.isPending) return;
    submitMutation.mutate({
      email,
      type: selectedTypeKey,
      content,
      images: images.length > 0 ? images : undefined,
    });
  };

  return (
    <Box
      sx={{
        flex: 1,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: c.bg,
      }}
    >
      {/* Notice Banner */}
      <Box sx={{ px: '20px', mt: '8px' }}>
        <Box
          sx={{
            bgcolor: c.bgNotice,
            borderRadius: '10px',
            px: '10px',
            py: '16px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              ...TEMP_TEXT_MEDIUM_M,
              color: c.primary,
              textAlign: 'center',
              whiteSpace: 'pre-line',
            }}
          >
            {`${t('inquiry.notice_1')}\n${t('inquiry.notice_2')}`}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              mt: '8px',
            }}
          >
            <Typography
              sx={{
                ...TEMP_TEXT_REGULAR_XS,
                color: c.fgLower,
              }}
            >
              {f(t('inquiry.contact_email'), ['contact@museling.me'])}
            </Typography>
            <ButtonBase
              onClick={() => {
                navigator.clipboard.writeText('contact@museling.me');
                showToast(t('inquiry.copied_title'), t('inquiry.copied_message'));
              }}
              sx={{
                p: '2px',
                borderRadius: '4px',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="9" y="9" width="11" height="11" rx="2" stroke={c.fgLower} strokeWidth="2" />
                <path d="M5 15H4C3.44772 15 3 14.5523 3 14V4C3 3.44772 3.44772 3 4 3H14C14.5523 3 15 3.44772 15 4V5" stroke={c.fgLower} strokeWidth="2" />
              </svg>
            </ButtonBase>
          </Box>
        </Box>
      </Box>

      {/* Form */}
      <Box
        sx={{
          px: '20px',
          mt: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          pb: '120px',
        }}
      >
        {/* Email Field */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Typography sx={{ ...TEMP_TEXT_MEDIUM_M, color: c.fgLow, px: '4px' }}>
            {t('inquiry.email_label')}
          </Typography>
          <Box
            sx={{
              border: `1px solid ${c.border}`,
              borderRadius: '16px',
              px: '16px',
              py: '16px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <InputBase
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('inquiry.email_placeholder')}
              type="email"
              fullWidth
              sx={{
                ...TEMP_TEXT_MEDIUM_XL,
                color: c.fg,
                '& input': {
                  padding: 0,
                },
                '& input::placeholder': {
                  color: c.fgLower,
                  opacity: 1,
                },
              }}
            />
          </Box>
        </Box>

        {/* Inquiry Type Field */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', position: 'relative' }}>
          <Typography sx={{ ...TEMP_TEXT_MEDIUM_M, color: c.fgLow, px: '4px' }}>
            {t('inquiry.type_label')}
          </Typography>
          <Box
            onClick={() => setShowTypeSelect(!showTypeSelect)}
            sx={{
              border: `1px solid ${c.border}`,
              borderRadius: '16px',
              px: '16px',
              py: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <Typography
              sx={{
                ...TEMP_TEXT_MEDIUM_XL,
                color: selectedTypeKey ? c.fg : c.fgLower,
              }}
            >
              {selectedTypeLabel ? getTypeLabel(selectedTypeLabel.label) : t('inquiry.type_placeholder')}
            </Typography>
            <Icon name="icChevronRight" size={20} color={c.fgLow} />
          </Box>
          {/* Type Dropdown - absolute overlay */}
          <TypeSelectDropdown
            open={showTypeSelect}
            isLoading={isLoadingTypes}
            types={inquiryTypes.map((type) => ({
              key: type.key,
              label: getTypeLabel(type.label),
            }))}
            selectedKey={selectedTypeKey}
            onSelect={(key) => {
              setSelectedTypeKey(key);
              setShowTypeSelect(false);
            }}
          />
        </Box>

        {/* Content Field */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Typography sx={{ ...TEMP_TEXT_MEDIUM_M, color: c.fgLow, px: '4px' }}>
            {t('inquiry.content_label')}
          </Typography>
          <Box
            sx={{
              border: `1px solid ${c.border}`,
              borderRadius: '16px',
              px: '20px',
              py: '16px',
              height: 260,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <InputBase
              value={content}
              onChange={(e) => {
                if (e.target.value.length <= MAX_CONTENT_LENGTH) {
                  setContent(e.target.value);
                }
              }}
              placeholder={t('inquiry.content_placeholder')}
              multiline
              fullWidth
              sx={{
                ...TEMP_TEXT_MEDIUM_XL,
                color: c.fg,
                flex: 1,
                alignItems: 'flex-start',
                '& textarea::placeholder': {
                  color: c.fgLower,
                  opacity: 1,
                },
              }}
            />
            <Typography
              sx={{
                ...TEMP_TEXT_MEDIUM_XL,
                color: c.fgLow,
                textAlign: 'right',
              }}
            >
              {content.length} / {MAX_CONTENT_LENGTH}
            </Typography>
          </Box>
        </Box>

        {/* Image Attachment */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: '4px',
              }}
            >
              <Typography sx={{ ...TEMP_TEXT_MEDIUM_M, color: c.fgLow }}>
                {t('inquiry.image_label')}
              </Typography>
              <Typography sx={{ ...TEMP_TEXT_REGULAR_XS, color: c.fgLower }}>
                {t('inquiry.image_max')}
              </Typography>
            </Box>

            {images.length < 3 && (
              <ButtonBase
                onClick={handleImageAdd}
                sx={{
                  border: `1px solid ${c.border}`,
                  borderRadius: '16px',
                  pl: '16px',
                  pr: '20px',
                  py: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                <Icon name="icPlus" size={16} color={c.fgLow} />
                <Typography sx={{ ...TEMP_TEXT_MEDIUM_M, color: c.fgLow }}>
                  {t('inquiry.image_add')}
                </Typography>
              </ButtonBase>
            )}
          </Box>

          {/* Image Previews - 추가 버튼 아래 */}
          {images.length > 0 && (
            <Box sx={{ display: 'flex', gap: '8px' }}>
              {images.map((img, idx) => (
                <Box
                  key={idx}
                  sx={{
                    position: 'relative',
                    width: 80,
                    height: 80,
                    borderRadius: '12px',
                    overflow: 'hidden',
                    flexShrink: 0,
                  }}
                >
                  <Box
                    component="img"
                    src={URL.createObjectURL(img)}
                    alt=""
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  <ButtonBase
                    onClick={() => handleRemoveImage(idx)}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      bgcolor: 'rgba(0,0,0,0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon name="icClose" size={12} color="#ffffff" />
                  </ButtonBase>
                </Box>
              ))}
            </Box>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
        </Box>


      </Box>

      {/* Submit Button - Fixed Bottom */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '100%',
          maxWidth: 'sm',
          px: '20px',
          pb: '34px',
          pt: '8px',
          bgcolor: c.bg,
        }}
      >
        <ButtonBase
          onClick={handleSubmit}
          disabled={!isFormValid || submitMutation.isPending}
          sx={{
            width: '100%',
            bgcolor: isFormValid ? c.primary : c.fgDisabled,
            borderRadius: '12px',
            px: '10px',
            py: '18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {submitMutation.isPending ? (
            <CircularProgress size={22} sx={{ color: '#ffffff' }} />
          ) : (
            <Typography sx={{ ...TEMP_TEXT_BOLD_XL, color: '#ffffff' }}>
              {t('inquiry.submit')}
            </Typography>
          )}
        </ButtonBase>
      </Box>
    </Box>
  );
}
